import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Chip,
  IconButton,
  Card,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";

const FormViewModal = ({ isOpen, onClose, formData }) => {
  if (!isOpen || !formData) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-IN", options);
  };

  const getFieldType = (question) => {
    if (question.field_type_name) {
      return question.field_type_name.toLowerCase();
    }
    const typeMapping = {
      1: "radio",
      2: "datepicker",
      3: "text",
      4: "checkbox",
    };
    return typeMapping[question.type] || "text";
  };

  const renderQuestion = (q, index, isNested = false, conditionText = "") => {
    const type = getFieldType(q);

    let optionsArray = [];
    if (Array.isArray(q.type_options)) {
      optionsArray = q.type_options;
    } else if (q.type_options && typeof q.type_options === "object") {
      optionsArray = Object.keys(q.type_options);
    }

    return (
      <Card
        key={q.question_id || index}
        elevation={0}
        sx={{
          mb: 3,
          mt: isNested ? 2 : 0,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "grey.200",
          backgroundColor: isNested ? "#f8fafc" : "#ffffff",
          boxShadow: isNested ? "none" : "0px 4px 12px rgba(0, 0, 0, 0.03)",
          position: "relative",
          overflow: "visible",
          ...(isNested && {
            borderLeft: "4px solid #4f46e5", // Indigo accent for nested conditions
          }),
        }}
      >
        {/* Conditional Tag */}
        {isNested && (
          <Box
            sx={{
              position: "absolute",
              top: -14,
              left: 16,
              display: "flex",
              alignItems: "center",
              bgcolor: "#e0e7ff",
              color: "#3730a3",
              px: 1.5,
              py: 0.5,
              borderRadius: "16px",
              border: "1px solid #c7d2fe",
              gap: 0.5,
            }}
          >
            <SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight="600">
              If {conditionText}
            </Typography>
          </Box>
        )}

        <Box sx={{ p: 3, pt: isNested ? 3.5 : 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: "600",
              color: "#1e293b",
              mb: 1,
              lineHeight: 1.5,
            }}
          >
            {isNested ? (
              ""
            ) : (
              <Box component="span" sx={{ color: "#64748b", mr: 1 }}>
                {index + 1}.
              </Box>
            )}
            {q.question || q.Question}
          </Typography>

          {q.key && (
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                bgcolor: "#f1f5f9",
                p: 1,
                borderRadius: "6px",
                display: "inline-block",
                mb: 2,
              }}
            >
              <Box component="span" fontWeight="600" mr={0.5}>
                Hint:
              </Box>
              {q.key}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            {type === "radio" && (
              <RadioGroup row>
                {optionsArray.map((opt, i) => (
                  <FormControlLabel
                    key={i}
                    value={opt}
                    control={
                      <Radio disabled size="small" sx={{ color: "grey.400" }} />
                    }
                    label={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight="500"
                      >
                        {opt}
                      </Typography>
                    }
                    sx={{ mr: 4, mb: 1 }}
                  />
                ))}
              </RadioGroup>
            )}

            {type === "checkbox" && (
              <FormGroup row>
                {optionsArray.map((opt, i) => (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        disabled
                        size="small"
                        sx={{ color: "grey.400" }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight="500"
                      >
                        {opt}
                      </Typography>
                    }
                    sx={{ mr: 4, mb: 1 }}
                  />
                ))}
              </FormGroup>
            )}

            {type === "text" && (
              <TextField
                fullWidth
                disabled
                size="small"
                placeholder="Text input (Read only)"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: "8px",
                  },
                }}
              />
            )}

            {type === "datepicker" && (
              <TextField
                type="date"
                fullWidth
                disabled
                size="small"
                variant="outlined"
                sx={{
                  maxWidth: "200px",
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: "8px",
                  },
                }}
              />
            )}
          </Box>

          {/* Render Nested Conditional Questions */}
          {!Array.isArray(q.type_options) &&
            typeof q.type_options === "object" &&
            q.type_options !== null && (
              <Box sx={{ mt: 1 }}>
                {Object.entries(q.type_options).map(
                  ([optionKey, nestedQuestion], i) => {
                    if (
                      nestedQuestion &&
                      (nestedQuestion.question || nestedQuestion.Question)
                    ) {
                      return (
                        <Box key={i} sx={{ pl: { xs: 0, sm: 2 } }}>
                          {renderQuestion(
                            nestedQuestion,
                            `${index}.${i}`,
                            true,
                            `answered "${optionKey}"`,
                          )}
                        </Box>
                      );
                    }
                    return null;
                  },
                )}
              </Box>
            )}
        </Box>
      </Card>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          bgcolor: "#f8fafc",
        },
      }}
    >
      {/* Modal Header - Updated to match Table Green */}
      <DialogTitle
        sx={{
          p: 0,
          bgcolor: "#6c8c71", // Green matching the table header
          color: "white",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}
            >
              <Typography variant="h5" fontWeight="700" letterSpacing="-0.5px">
                {formData.form_name || "Form Preview"}
              </Typography>
              <Chip
                label="Read Only"
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  fontWeight: 600,
                  backdropFilter: "blur(4px)",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                color: "rgba(255,255,255,0.85)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight="500">
                  {formatDate(formData.added_date)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight="500">
                  Shifts: {formData.shifts || 0}
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Modal Body */}
      <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: "#f1f5f9" }}>
        {formData.QuestionForm && formData.QuestionForm.length > 0 ? (
          formData.QuestionForm.map((q, index) =>
            renderQuestion(q, index, false),
          )
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary" fontWeight="500">
              No questions found in this form.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* Modal Footer */}
      <Divider />
      <DialogActions
        sx={{ p: 2.5, bgcolor: "#ffffff", justifyContent: "center" }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            bgcolor: "#6c8c71", // Green matching the header
            color: "white",
            textTransform: "none",
            fontWeight: "600",
            fontSize: "0.95rem",
            px: 5,
            py: 1,
            borderRadius: "8px",
            "&:hover": { bgcolor: "#59785d" }, // Slightly darker green on hover
          }}
        >
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormViewModal;
